import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";

import WithAppLayout from "../../components/WithAppLayout";

import { EDIT_ROLES_PATH, COPY_ROLES_PATH } from "../../routes";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import __ from "../../helpers/translations";
import { getLocalStorageItem } from "../../helpers/utils";
import {
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  HStack,
  Button,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { Select } from "chakra-react-select";
import { FiSearch } from "react-icons/fi";
import PaginationTable from "../../components/PaginationTable/PaginationTable";
import { COLUMN_PRIORITY } from "../../components/PageTableWithSearch/tableDataHelper";
import { createColumnHelper } from "@tanstack/react-table";
import { ALL_CERTIFICATIONS_QUERY, ALL_SKILLS_QUERY, GET_ROLES_QUERY } from "../../graphql/admin";
const columnHelper = createColumnHelper();

function RolesPageInner({
  currentUser,
  loading,
}) {
  const LS_KEY = "admin-candidate-roles-filters";
  const LS_PAGE_KEY = "admin-candidate-roles-pagination";
  const localStorage = window.localStorage;
  const [certifications, setCertifications] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [skills, setSkills] = useState([]);
  const defaultFilters = getLocalStorageItem(LS_KEY, {
    search: "",
    certifications: [],
    skills: [],
  });
  const [filters, setFilters] = useState(defaultFilters);

  const defaultPagination = getLocalStorageItem(LS_PAGE_KEY, { pageIndex: 0, pageSize: 10 });
  const [pagination, setPagination] = useState(defaultPagination);
  const setFiltersAndSave = useCallback((patchFilters) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, ...patchFilters };
      window.localStorage.setItem(LS_KEY, JSON.stringify(newFilters));
      return newFilters;
    });
  }, []);
  const savePagination = (data) => {
    data && localStorage.setItem(LS_PAGE_KEY, JSON.stringify(data));
  };
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search); // New state for debounced search
  const navigate = useNavigate();

  useEffect(() => {
    const handler = setTimeout(() => {
      setFiltersAndSave({ search: debouncedSearch });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearch, setFiltersAndSave]);

  const { loading: skillsLoading } = useQuery(ALL_SKILLS_QUERY, {
    onCompleted: (data) => {
      setSkills(data.allSkills);
      const selectedSkills = data.allSkills.filter(skill => filters.skills.includes(skill.id));
      setSelectedSkills(selectedSkills.map(skill => ({ label: skill.name, value: skill.id })));
    },
  });

  const { loading: certificationsLoading } = useQuery(ALL_CERTIFICATIONS_QUERY, {
    onCompleted: (data) => {
      setCertifications(data.allCertifications);
      const selectedCertifications = data.allCertifications.filter(cert => filters.certifications.includes(cert.id));
      setSelectedCertifications(selectedCertifications.map(cert => ({ label: cert.name, value: cert.id })));
    },
  });

  if (loading || skillsLoading || certificationsLoading) {
    return <LoadingIndicator />;
  }

  const MultipleItemCell = ({ items, limit = 0 }) => {
    if (!items) {
      return null;
    }
    const limitedItems = limit > 0 ? items.slice(0, limit) : items;
    const remainingItems = limit > 0 ? items.slice(limit) : [];
    if (remainingItems.length > 0) {
      limitedItems.push(`+${remainingItems.length}`);
    }
    return (
      <HStack gap="2px">
        {limitedItems.map((item, idx) => (
          <Text
            key={idx}
            fontSize="sm"
            color="gray.50"
            p="4px 8px"
            bg="whiteAlpha.300"
            borderRadius="6px"
          >
            {item.length > 15 ? `${item.slice(0, 15)}...` : item}
          </Text>
        ))}
      </HStack>
    );
  };
  const NameCell = ({ row }) => {
    return (
      <Stack gap={0}>
        <Text color="whiteAlpha.800">
          {row.original.name.length > 30 ? `${row.original.name.slice(0, 30)}...` : row.original.name}
        </Text>
      </Stack>
    )
  };
  const ActionCell = ({ row }) => {
    return (
      <HStack gap={2}>
        <Button
          variant="outline"
          size="xs"
          colorScheme="whiteAlpha"
          onClick={(e) => {
            e.stopPropagation();
            navigate(EDIT_ROLES_PATH.replace(":id", row.original.id));
          }}
        >
          Edit
        </Button>
        <Button
          variant="outline"
          size="xs"
          colorScheme="whiteAlpha"
          onClick={(e) => {
            e.stopPropagation();
            navigate(COPY_ROLES_PATH.replace(":id", row.original.id));
          }}
        >
          Copy
        </Button>
      </HStack>
    )
  };
  const candidatesColumns = [
    columnHelper.accessor("name", {
      cell: (info) => NameCell({ row: info.row }),
      header: "Name",
      meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
    }),
    columnHelper.accessor(
      (item) =>
        item?.certifications || [],
      {
        // Accessor functions need an explicit id for the responsive
        // collapsing and the Columns menu to address the column.
        id: "certifications",
        cell: (info) => (<MultipleItemCell items={info.getValue()} limit={2} />),
        header: "Certifications",
        meta: { priority: COLUMN_PRIORITY.IMPORTANT },
      }
    ),
    columnHelper.accessor(
      (item) =>
        item?.skills || [],
      {
        id: "skills",
        cell: (info) => (<MultipleItemCell items={info.getValue()} limit={3} />),
        header: "Skills",
        meta: { priority: COLUMN_PRIORITY.SECONDARY },
      }
    ),
    columnHelper.accessor("actions", {
      cell: (info) => ActionCell({ row: info.row }),
      header: "Actions",
      // Edit/Copy are the row's only affordances, so they stay on screen at
      // every width and cannot be hidden from the Columns menu.
      enableHiding: false,
      meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
    }),
  ];

  const filterInputs = (
    <HStack spacing={4} alignItems="center">
      <HStack>
        <InputGroup maxW="xs">
          <Input
            placeholder="Search"
            value={debouncedSearch}
            onChange={(e) => setDebouncedSearch(e.target.value)}
            variant="black"
          />
          <InputRightElement pointerEvents="none">
            <Icon as={FiSearch} color="white" boxSize="5" />
          </InputRightElement>
        </InputGroup>
      </HStack>
      <HStack spacing={4} ml={"auto"}>
        <Select
          isMulti={true}
          defaultValue={selectedCertifications}
          options={certifications.map((cert) => ({ label: cert.name, value: cert.id }))}
          onChange={(selected) => {
            const selectedValues = selected.map((option) => option.value);
            setFiltersAndSave({ certifications: selectedValues });
          }}
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              minWidth: "300px"
            })
          }}
          placeholder="Select Certification"
        />
        <Select
          isMulti={true}
          defaultValue={selectedSkills}
          options={skills.map((skill) => ({ label: skill.name, value: skill.id }))}
          onChange={(selected) => {
            const selectedValues = selected.map((option) => option.value);
            setFiltersAndSave({ skills: selectedValues });
          }}
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              minWidth: "300px"
            })
          }}
          placeholder="Select Skill"
        />
      </HStack>
    </HStack>
  );
  const title = (
    <HStack spacing={4} alignItems="center">
      <Text fontSize="3xl" fontWeight="bold" color="white">
        {__("Candidate Roles", currentUser?.agency?.type)}
      </Text>
      <Button
        leftIcon={<AddIcon />}
        colorScheme="gray"
        variant="solid"
        onClick={() => navigate(EDIT_ROLES_PATH.replace(":id", "new"))}
      >
        Add New Role
      </Button>
    </HStack>
  );

  return (
    <PaginationTable
      title={title}
      tableId="admin-candidate-roles"
      columns={candidatesColumns}
      query={GET_ROLES_QUERY}
      queryName="paginatedCandidateRoles"
      itemKey="roles"
      filters={filters}
      paginationState={[pagination, setPagination, savePagination]}
      filterInputs={filterInputs}
      onClickRow={(role) => {
        navigate(EDIT_ROLES_PATH.replace(":id", role.id));
      }}
    >
    </PaginationTable>
  );
}

export default WithAppLayout(RolesPageInner);
